import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const TEMPLATE_TYPES = [
  { id: 'quote_sent', label: 'Quote Sent', icon: 'FileText', description: 'Sent when a quote is prepared for a customer' },
  { id: 'quote_approved', label: 'Quote Approved', icon: 'CheckCircle', description: 'Sent when a customer approves a quote' },
  { id: 'quote_rejected', label: 'Quote Rejected', icon: 'XCircle', description: 'Sent when a quote is rejected' },
  { id: 'ticket_created', label: 'Ticket Created', icon: 'ClipboardList', description: 'Confirmation sent when a ticket is created' },
  { id: 'ticket_assigned', label: 'Ticket Assigned', icon: 'UserCog', description: 'Sent when a technician is assigned to a ticket' },
  { id: 'ticket_completed', label: 'Ticket Completed', icon: 'CheckSquare', description: 'Sent when a ticket is marked complete' },
  { id: 'assignment_notification', label: 'Assignment Alert', icon: 'Bell', description: 'Sent to technicians when assigned a job' },
  { id: 'survey_request', label: 'Survey Request', icon: 'Star', description: 'Sent to customers requesting satisfaction feedback' },
  { id: 'invoice_sent', label: 'Invoice Sent', icon: 'Receipt', description: 'Sent to customers when an invoice is issued' },
  { id: 'payment_received', label: 'Payment Received', icon: 'CheckCircle2', description: 'Confirmation sent when a payment is recorded' },
  { id: 'payment_reminder', label: 'Payment Reminder', icon: 'Clock', description: 'Reminder sent before invoice due date' },
  { id: 'overdue_notice', label: 'Overdue Notice', icon: 'AlertTriangle', description: 'Sent when an invoice becomes overdue' },
];

const DEFAULT_VARIABLES = {
  quote_sent: ['customer_name', 'quote_number', 'total_amount', 'company_name', 'logo_url'],
  quote_approved: ['customer_name', 'quote_number', 'company_name', 'logo_url'],
  quote_rejected: ['customer_name', 'quote_number', 'reason', 'company_name', 'logo_url'],
  ticket_created: ['customer_name', 'ticket_number', 'description', 'priority', 'company_name', 'logo_url'],
  ticket_assigned: ['customer_name', 'ticket_number', 'technician_name', 'scheduled_date', 'company_name', 'logo_url'],
  ticket_completed: ['customer_name', 'ticket_number', 'technician_name', 'completion_date', 'company_name', 'logo_url'],
  assignment_notification: ['technician_name', 'ticket_number', 'customer_name', 'location', 'scheduled_date', 'company_name', 'logo_url'],
  survey_request: ['customer_name', 'ticket_number', 'survey_link', 'company_name', 'logo_url'],
  invoice_sent: ['customer_name', 'invoice_number', 'invoice_date', 'due_date', 'total_amount', 'company_name', 'logo_url'],
  payment_received: ['customer_name', 'invoice_number', 'payment_amount', 'payment_method', 'payment_date', 'remaining_balance', 'company_name', 'logo_url'],
  payment_reminder: ['customer_name', 'invoice_number', 'due_date', 'amount_due', 'company_name', 'logo_url'],
  overdue_notice: ['customer_name', 'invoice_number', 'due_date', 'days_overdue', 'amount_due', 'company_name', 'logo_url'],
};

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState('quote_sent');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [orgId, setOrgId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const [editForm, setEditForm] = useState({
    name: '',
    subject: '',
    body_html: '',
    body_text: '',
    is_active: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const orgRes = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
      if (orgRes?.error) throw orgRes?.error;
      setOrgId(orgRes?.data?.id);

      const { data, error: tmplError } = await supabase?.from('email_templates')?.select('*')?.eq('organization_id', orgRes?.data?.id);
      if (tmplError) throw tmplError;
      setTemplates(data || []);

      // Set initial edit form
      const firstTemplate = data?.find(t => t?.template_type === 'quote_sent');
      if (firstTemplate) {
        setEditForm({
          name: firstTemplate?.name,
          subject: firstTemplate?.subject,
          body_html: firstTemplate?.body_html,
          body_text: firstTemplate?.body_text || '',
          is_active: firstTemplate?.is_active,
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load email templates.');
    } finally {
      setLoading(false);
    }
  };

  const currentTemplate = templates?.find(t => t?.template_type === selectedType);
  const currentTypeInfo = TEMPLATE_TYPES?.find(t => t?.id === selectedType);
  const currentVariables = DEFAULT_VARIABLES?.[selectedType] || [];

  const handleSelectType = (typeId) => {
    setSelectedType(typeId);
    setPreviewMode(false);
    setSuccessMsg('');
    const tmpl = templates?.find(t => t?.template_type === typeId);
    if (tmpl) {
      setEditForm({
        name: tmpl?.name,
        subject: tmpl?.subject,
        body_html: tmpl?.body_html,
        body_text: tmpl?.body_text || '',
        is_active: tmpl?.is_active,
      });
    } else {
      setEditForm({ name: '', subject: '', body_html: '', body_text: '', is_active: true });
    }
  };

  const insertVariable = (variable) => {
    const tag = `{{${variable}}}`;
    setEditForm(prev => ({ ...prev, body_html: prev?.body_html + tag }));
  };

  const handleSave = async () => {
    if (!editForm?.name || !editForm?.subject || !editForm?.body_html) {
      setError('Name, subject, and body are required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      if (currentTemplate) {
        const { error } = await supabase?.from('email_templates')?.update({
            name: editForm?.name,
            subject: editForm?.subject,
            body_html: editForm?.body_html,
            body_text: editForm?.body_text,
            is_active: editForm?.is_active,
            updated_at: new Date()?.toISOString(),
          })?.eq('id', currentTemplate?.id);
        if (error) throw error;
      } else {
        const { error } = await supabase?.from('email_templates')?.insert({
            organization_id: orgId,
            template_type: selectedType,
            name: editForm?.name,
            subject: editForm?.subject,
            body_html: editForm?.body_html,
            body_text: editForm?.body_text,
            is_active: editForm?.is_active,
            variables: JSON.stringify(currentVariables),
          });
        if (error) throw error;
      }
      await fetchTemplates();
      setSuccessMsg('Template saved successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (currentTemplate) {
      setEditForm({
        name: currentTemplate?.name,
        subject: currentTemplate?.subject,
        body_html: currentTemplate?.body_html,
        body_text: currentTemplate?.body_text || '',
        is_active: currentTemplate?.is_active,
      });
    }
    setError('');
  };

  // Preview: replace variables with sample values
  const sampleValues = {
    customer_name: 'John Smith',
    quote_number: 'QT-2024-001',
    total_amount: '1,250.00',
    company_name: 'RIGJOCKEY Services',
    logo_url: '',
    ticket_number: 'TK-2024-042',
    description: 'HVAC maintenance and inspection',
    priority: 'High',
    technician_name: 'Mike Johnson',
    scheduled_date: 'March 15, 2024 at 9:00 AM',
    completion_date: 'March 15, 2024',
    location: '123 Oil Field Rd, Midland TX',
    survey_link: 'https://app.rigjockey.com/survey/abc123',
    reason: 'Budget constraints',
    invoice_number: 'INV-2024-0042',
    invoice_date: 'February 26, 2024',
    due_date: 'March 26, 2024',
    payment_amount: '850.00',
    payment_method: 'Credit Card',
    payment_date: 'February 28, 2024',
    remaining_balance: '400.00',
    amount_due: '1,250.00',
    days_overdue: '15',
  };

  const getPreviewHtml = () => {
    let html = editForm?.body_html;
    Object.entries(sampleValues)?.forEach(([key, val]) => {
      html = html?.replace(new RegExp(`{{${key}}}`, 'g'), `<strong style="color:#2563eb">${val}</strong>`);
    });
    return html;
  };

  const getPreviewSubject = () => {
    let subject = editForm?.subject;
    Object.entries(sampleValues)?.forEach(([key, val]) => {
      subject = subject?.replace(new RegExp(`{{${key}}}`, 'g'), val);
    });
    return subject;
  };

  return (
    <>
      <Helmet><title>Email Templates - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-heading font-bold text-foreground">Email Templates</h1>
            <p className="text-muted-foreground text-sm mt-1">Customize notification emails sent to customers and technicians.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
              <button onClick={() => setError('')} className="ml-auto"><Icon name="X" size={14} color="var(--color-destructive)" /></button>
            </div>
          )}

          {successMsg && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <p className="text-sm text-success">{successMsg}</p>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Template Type Sidebar */}
            <div className="lg:w-72 flex-shrink-0">
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-foreground text-sm">Template Types</h3>
                </div>
                <div className="divide-y divide-border">
                  {TEMPLATE_TYPES?.map(type => {
                    const tmpl = templates?.find(t => t?.template_type === type?.id);
                    return (
                      <button
                        key={type?.id}
                        onClick={() => handleSelectType(type?.id)}
                        className={`w-full text-left p-4 transition-smooth hover:bg-muted/50 ${
                          selectedType === type?.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selectedType === type?.id ? 'bg-primary/10' : 'bg-muted'
                          }`}>
                            <Icon name={type?.icon} size={15} color={selectedType === type?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium truncate ${
                              selectedType === type?.id ? 'text-primary' : 'text-foreground'
                            }`}>{type?.label}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {tmpl ? (
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                  tmpl?.is_active ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'
                                }`}>{tmpl?.is_active ? 'Active' : 'Inactive'}</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">Not configured</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Editor */}
            <div className="flex-1">
              {loading ? (
                <div className="bg-card rounded-xl border border-border p-12 text-center">
                  <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin mx-auto mb-3" />
                  <p className="text-muted-foreground">Loading templates...</p>
                </div>
              ) : (
                <div className="bg-card rounded-xl border border-border">
                  {/* Editor Header */}
                  <div className="p-5 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={currentTypeInfo?.icon || 'Mail'} size={18} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{currentTypeInfo?.label}</h3>
                        <p className="text-xs text-muted-foreground">{currentTypeInfo?.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPreviewMode(!previewMode)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
                          previewMode ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-muted'
                        }`}
                      >
                        <Icon name="Eye" size={14} color="currentColor" />
                        {previewMode ? 'Edit' : 'Preview'}
                      </button>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-sm text-muted-foreground">Active</span>
                        <div
                          onClick={() => setEditForm(p => ({...p, is_active: !p?.is_active}))}
                          className={`w-10 h-5 rounded-full transition-smooth cursor-pointer ${
                            editForm?.is_active ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-smooth ${
                            editForm?.is_active ? 'ml-5.5' : 'ml-0.5'
                          }`} style={{ marginLeft: editForm?.is_active ? '22px' : '2px' }} />
                        </div>
                      </label>
                    </div>
                  </div>

                  {previewMode ? (
                    /* Preview Mode */
                    (<div className="p-6">
                      <div className="bg-muted/30 rounded-lg p-4 mb-4">
                        <p className="text-xs text-muted-foreground mb-1">Subject Preview</p>
                        <p className="font-medium text-foreground">{getPreviewSubject() || '(no subject)'}</p>
                      </div>
                      <div className="border border-border rounded-lg overflow-hidden">
                        <div className="bg-muted/30 px-4 py-2 border-b border-border">
                          <p className="text-xs text-muted-foreground">Email Body Preview — <span className="text-primary">highlighted values</span> are sample data</p>
                        </div>
                        <div
                          className="p-6 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                        />
                      </div>
                    </div>)
                  ) : (
                    /* Edit Mode */
                    (<div className="p-6 space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Template Name <span className="text-destructive">*</span></label>
                        <input
                          type="text"
                          value={editForm?.name}
                          onChange={e => setEditForm(p => ({...p, name: e?.target?.value}))}
                          placeholder="e.g. Quote Sent Notification"
                          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Email Subject <span className="text-destructive">*</span></label>
                        <input
                          type="text"
                          value={editForm?.subject}
                          onChange={e => setEditForm(p => ({...p, subject: e?.target?.value}))}
                          placeholder="e.g. Your Quote {{quote_number}} is Ready"
                          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                      {/* Variables */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Available Variables</label>
                        <div className="flex flex-wrap gap-2">
                          {currentVariables?.map(v => (
                            <button
                              key={v}
                              onClick={() => insertVariable(v)}
                              title="Click to insert into body"
                              className="px-2.5 py-1 bg-primary/10 text-primary text-xs font-mono rounded-md hover:bg-primary/20 transition-smooth"
                            >
                              {`{{${v}}}`}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Click a variable to insert it into the email body.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Email Body (HTML) <span className="text-destructive">*</span></label>
                        <textarea
                          value={editForm?.body_html}
                          onChange={e => setEditForm(p => ({...p, body_html: e?.target?.value}))}
                          rows={12}
                          placeholder="<p>Dear {{customer_name}},</p><p>Your quote is ready...</p>"
                          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                        />
                        <p className="text-xs text-muted-foreground mt-1">HTML is supported. Use variables like {`{{customer_name}}`} for dynamic content.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Plain Text Version</label>
                        <textarea
                          value={editForm?.body_text}
                          onChange={e => setEditForm(p => ({...p, body_text: e?.target?.value}))}
                          rows={4}
                          placeholder="Plain text fallback for email clients that don't support HTML..."
                          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                        />
                      </div>
                    </div>)
                  )}

                  {/* Footer Actions */}
                  {!previewMode && (
                    <div className="px-6 py-4 border-t border-border flex items-center justify-between">
                      <button
                        onClick={handleReset}
                        className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-smooth"
                      >
                        <Icon name="RotateCcw" size={14} color="currentColor" />
                        Reset Changes
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-smooth disabled:opacity-50"
                      >
                        {saving && <Icon name="Loader2" size={14} color="white" className="animate-spin" />}
                        <Icon name="Save" size={14} color="white" />
                        Save Template
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default EmailTemplates;
